import React, { forwardRef, useImperativeHandle, useMemo } from 'react'
import './send-notification-editor.scss'
import { Skeleton } from 'antd'
import { useJourneyContext } from '../context'
import { NotificationDto } from '../../notifications'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../stores/app'
import { NotificationService } from '../../../services'
import NotificationBuilder from '../../../components/notification-builder/notification-builder'
import { NotificationBuilderLevel } from '../../../components/notification-builder/enums'
import {
    buildPreviewOptions,
    buildSubmittedNotificationPackages,
    validateSubmit as validateNotificationBuilder,
} from '../../../components/notification-builder/helpers'
import {
    ActionStep,
    getSendParams,
    isSendWithItemStrategyActionStep,
    JourneyNode,
    NodeEditorRef,
} from '../types/journey-nodes'
import { getJourneyTriggerItemType } from '../node-types/custom-node/helpers'
import { useItemStrategyOptions } from '../hooks/use-item-strategy-options'
import { reconcileSelectedStrategy } from '../selectors/catalog-item-pick-strategy'
import NotificationBuilderContent from './notification-builder-content'
import { useNotificationPreview } from '../hooks/use-notification-preview'
import { useSubmitAction } from '../hooks/use-submit-action'
import type { JourneyNotificationBuilderConfig } from './types'
import type { CombinedItemStrategy, CatalogItemPickStrategy, ItemRankingStrategy } from '@pushly/models/lib/enums/catalogs'
import { MacroType } from '@pushly/aqe/lib/components'
import { TriggerType } from '@pushly/models/lib/enums/journeys'

function arraysShallowEqual<T>(a: T[], b: T[]) {
    return a.length === b.length && a.every((v, i) => v === b[i])
}

// Helper to reconcile selected strategy for CombinedItemStrategy
function reconcileSelectedCombinedStrategy(
    nextOptions: CombinedItemStrategy[],
    current?: CombinedItemStrategy,
): CombinedItemStrategy | undefined {
    if (!nextOptions || nextOptions.length === 0) return undefined
    // If current is valid, keep it; otherwise default to first option
    return current && nextOptions.includes(current) ? current : nextOptions[0]
}

type SendNotificationEditorProps = {
    node: JourneyNode<ActionStep>
    macroCategories?: { urlFields?: MacroType[]; messageFields?: MacroType[] }
    imageMacroToggleLabel?: string
    imageMacroValue?: string
    imageMacroIcon?: React.ReactNode
    builderProps?: Partial<JourneyNotificationBuilderConfig>
}

export const SendNotificationEditor = forwardRef<NodeEditorRef, SendNotificationEditorProps>((props, ref) => {
    const { node } = props

    const [state, _dispatch] = useJourneyContext()

    const appState = useService(AppState)
    const notifService = useService(NotificationService)

    const journey = state.journey
    const domain = state.domain

    const action = node.data.step

    const sendParams = getSendParams(action)
    const notification = sendParams?.notification

    const hasItemStrategy = isSendWithItemStrategyActionStep(action)
    const currentSelectedStrategy = hasItemStrategy && journey ? action.configuration.params.item_strategy : undefined

    // Get trigger type for context-aware labels
    const triggerType = journey?.configuration?.computed_trigger_type as TriggerType | undefined

    // For trigger-based campaigns, get itemType from trigger; for scheduled, use domain's ecommConfig
    const itemType = hasItemStrategy && journey
        ? (getJourneyTriggerItemType(journey) ?? domain?.ecommConfig?.itemType)
        : undefined
    const itemStrategy = useItemStrategyOptions({
        action,
        itemType,
        allNodes: state.nodes,
        currentNodeId: node.id,
        currentSelectedStrategy,
    })

    const builderConfig: JourneyNotificationBuilderConfig = useMemo(() => {
        const config: JourneyNotificationBuilderConfig = {
            domain,
            level: NotificationBuilderLevel.CAMPAIGN,
            mode: notification ? 'edit' : 'create',
            notifications: notification ? [NotificationDto.parse(notification)] : undefined,
            defaultChannels: state.journey?.channels,
            defaultSelectedNotifId: notification?.id ? Number(notification.id) : undefined,
            showWebhookOptions: false,
        }

        if (itemStrategy.isCatalog) {
            config.catalogItemType = itemType ?? undefined
            config.catalogItemPickStrategyOptions = itemStrategy.options
            config.selectedCatalogItemPickStrategy = itemStrategy.selected

            config.macroProps = {
                categories: {
                    urlFields: ['domain', 'device', 'location', 'ecomm', 'notification', 'custom'],
                    messageFields: ['location', 'ecomm', 'custom'],
                },
                imageMacroProps: {
                    label: itemStrategy.templates.imageMacroTagLabel,
                    tag: itemStrategy.templates.imageMacroTag,
                },
            }

            config.onInitialVariantInit = (variant) => {
                const contentModel = variant.getContent().getDefaultContent()
                contentModel.setLandingUrl(itemStrategy.templates.url)
                contentModel.setTitle(itemStrategy.templates.title)
                contentModel.setBody(itemStrategy.templates.body)
                contentModel.setImageUrl(itemStrategy.templates.image)
            }
        }

        return config
    }, [itemStrategy, itemType, notification, state.journey?.channels, domain])

    // Show catalog item options if we have a catalog item type OR if we have item strategy options
    const isCatalogItemNotification = !!builderConfig.catalogItemType || (itemStrategy.isCatalog && itemStrategy.options.length > 0)
    if (!domain) return <></>

    const builderProps = {
        ...builderConfig,
        ...props.builderProps,
    }

    return (
        <div id="send-notification-editor">
            <Skeleton
                loading={!state.domain}
                active={true}
                title={false}
                avatar={false}
                paragraph={{
                    rows: 1,
                    width: '100%',
                }}
            >
                {!state.domain ? (
                    <></>
                ) : (
                    <NotificationBuilder {...builderProps}>
                        {(notificationBuilderProps) => {
                            const { builder, dispatchChanges, validateSubmit, variants, onVariantChange } =
                                notificationBuilderProps

                            const options = buildPreviewOptions(builder.channels)

                            // Sync builder options/selection with derived values
                            React.useEffect(() => {
                                if (!isCatalogItemNotification) return

                                const nextOptions = builderProps.catalogItemPickStrategyOptions
                                if (!nextOptions) return

                                const currOptions = builder.catalogItemPickStrategyOptions
                                const currSelected = builder.selectedCatalogItemPickStrategy

                                const optionsEqual = arraysShallowEqual(currOptions ?? [], nextOptions)
                                const nextSelected = reconcileSelectedCombinedStrategy(nextOptions, currSelected)
                                const selectedEqual = currSelected === nextSelected

                                if (optionsEqual && selectedEqual) return

                                dispatchChanges({
                                    type: 'patch',
                                    entity: 'root',
                                    data: {
                                        catalogItemPickStrategyOptions: nextOptions,
                                        selectedCatalogItemPickStrategy: nextSelected,
                                    },
                                })
                            }, [
                                isCatalogItemNotification,
                                builderProps.catalogItemPickStrategyOptions,
                                builder.selectedCatalogItemPickStrategy,
                                dispatchChanges,
                            ])

                            const onPreview = useNotificationPreview(
                                domain,
                                notifService,
                                builder,
                                appState.flags,
                                {
                                    campaign_id: state.journey?.id,
                                    campaign_notification_type: isCatalogItemNotification
                                        ? 'cart_notification'
                                        : 'standard_notification',
                                },
                                validateSubmit,
                                buildSubmittedNotificationPackages,
                            )

                            const onSubmit = useSubmitAction(
                                builder,
                                domain,
                                appState.flags,
                                isCatalogItemNotification,
                                node,
                                state.mode,
                                validateNotificationBuilder,
                                buildSubmittedNotificationPackages,
                            )

                            useImperativeHandle(ref, () => ({ onSubmit }))

                            const handleCatalogStrategyChange = React.useCallback(
                                (strategy: CombinedItemStrategy) =>
                                    dispatchChanges({
                                        type: 'patch',
                                        entity: 'root',
                                        data: { selectedCatalogItemPickStrategy: strategy },
                                    }),
                                [dispatchChanges],
                            )

                            // Compute selected value in a type-safe way
                            const availableOptions = builderProps.catalogItemPickStrategyOptions
                            const builderSelected = builder.selectedCatalogItemPickStrategy
                            const selectedValue =
                                availableOptions && builderSelected && availableOptions.includes(builderSelected)
                                    ? builderSelected
                                    : undefined

                            return (
                                <NotificationBuilderContent
                                    domain={domain}
                                    builder={builder}
                                    variants={variants}
                                    dispatchChanges={dispatchChanges}
                                    onVariantChange={onVariantChange}
                                    previewOptions={options}
                                    onPreviewRequest={onPreview}
                                    isCatalogItemNotification={isCatalogItemNotification}
                                    catalogItemPickStrategyOptions={availableOptions ?? []}
                                    selectedCatalogItemPickStrategy={selectedValue}
                                    onCatalogStrategyChange={handleCatalogStrategyChange}
                                    catalogItemType={itemType}
                                    triggerType={triggerType}
                                    macroProps={builderProps.macroProps}
                                />
                            )
                        }}
                    </NotificationBuilder>
                )}
            </Skeleton>
        </div>
    )
})

export default SendNotificationEditor
